import React, { useEffect, useMemo, useState } from "react";
import { View, Text, TextInput, Pressable, FlatList, ActivityIndicator, Alert, StyleSheet } from "react-native";
import { auth, db } from "@/lib/firebase";
import NamePromptModal from "@/components/admin/NamePromptModal";
import {
  type ModerationStatus,
  adminListCounties,
  adminListCities,
  adminCreateApprovedCity,
  adminCityNameExists,
  adminRenameCity,
  adminApproveCity,
  adminRejectCity,
  adminSoftDeleteCity,
  adminRestoreCity,
} from "@/lib/locationsAdmin";

type County = any;
type City = any;

const STATUS_TABS: ModerationStatus[] = ["approved", "pending", "rejected"];

export default function AdminCitiesManager() {
  const adminUid = auth.currentUser?.uid;

  const [counties, setCounties] = useState<County[]>([]);
  const [countyLoading, setCountyLoading] = useState(true);
  const [selectedCountyId, setSelectedCountyId] = useState<string | null>(null);

  const [status, setStatus] = useState<ModerationStatus>("approved");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<City[]>([]);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editing, setEditing] = useState<City | null>(null);

  const selectedCounty = useMemo(
    () => counties.find((c) => c.id === selectedCountyId) ?? null,
    [counties, selectedCountyId]
  );

  function requireCountyId(): string {
    const id = selectedCountyId ?? "";
    if (!id) throw new Error("Pick a county first.");
    return id;
  }

  async function loadCounties() {
    if (!adminUid) return;
    setCountyLoading(true);
    try {
      const data = await adminListCounties(db);
      setCounties(data);
      if (!selectedCountyId && data.length) setSelectedCountyId(data[0].id);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to load counties.");
    } finally {
      setCountyLoading(false);
    }
  }

  async function loadCities() {
    if (!adminUid) return;
    if (!selectedCountyId) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await adminListCities(db, {
        countyId: selectedCountyId,
        status,
        includeDeleted,
      });
      setRows(data);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to load cities.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCounties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid]);

  useEffect(() => {
    loadCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid, selectedCountyId, status, includeDeleted]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r?.name ?? "").toLowerCase().includes(q));
  }, [rows, search]);

  function openAdd() {
    if (!selectedCountyId) {
      Alert.alert("Select county", "Pick a county first.");
      return;
    }
    setModalMode("add");
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(city: City) {
    setModalMode("edit");
    setEditing(city);
    setModalOpen(true);
  }

  async function handleSaveName(name: string) {
    if (!adminUid) return;

    try {
      const countyId = requireCountyId();

      if (modalMode === "add") {
        const exists = await adminCityNameExists(db, countyId, name);
        if (exists) {
          Alert.alert("Already exists", `"${name}" already exists in this county.`);
          return;
        }

        await adminCreateApprovedCity(db, { countyId, name, adminUid });
      } else if (editing?.id) {
        await adminRenameCity(db, { countyId, cityId: editing.id, name, adminUid });
      }

      setModalOpen(false);
      await loadCities();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to save.");
    }
  }

  async function approve(city: City) {
    if (!adminUid) return;
    try {
      const countyId = requireCountyId();
      await adminApproveCity(db, { countyId, cityId: city.id, adminUid });
      await loadCities();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Approve failed.");
    }
  }

  async function reject(city: City) {
    if (!adminUid) return;
    Alert.alert("Reject city?", `Reject "${city.name}"?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Reject",
        style: "destructive",
        onPress: async () => {
          try {
            const countyId = requireCountyId();
            await adminRejectCity(db, { countyId, cityId: city.id, adminUid });
            await loadCities();
          } catch (e: any) {
            Alert.alert("Error", e?.message ?? "Reject failed.");
          }
        },
      },
    ]);
  }

  async function toggleDelete(city: City) {
    if (!adminUid) return;

    try {
      const countyId = requireCountyId();

      if (city.isDeleted) {
        await adminRestoreCity(db, { countyId, cityId: city.id, adminUid });
        await loadCities();
        return;
      }

      Alert.alert("Delete city?", `Soft delete "${city.name}"?`, [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            try {
              await adminSoftDeleteCity(db, { countyId, cityId: city.id, adminUid });
              await loadCities();
            } catch (e: any) {
              Alert.alert("Error", e?.message ?? "Delete failed.");
            }
          },
        },
      ]);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Action failed.");
    }
  }

  return (
    <View style={{ flex: 1 }}>
      {/* County selector */}
      {countyLoading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading counties…</Text>
        </View>
      ) : (
        <View style={styles.selectorRow}>
          <Text style={styles.selectorLabel}>County:</Text>
          <View style={styles.selectorWrap}>
            <FlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              data={counties}
              keyExtractor={(i) => i.id}
              contentContainerStyle={{ gap: 8 }}
              renderItem={({ item }) => {
                const active = item.id === selectedCountyId;
                return (
                  <Pressable
                    onPress={() => setSelectedCountyId(item.id)}
                    style={[styles.catPill, active && styles.catPillActive]}
                  >
                    <Text style={[styles.catPillText, active && styles.catPillTextActive]}>
                      {item.name}
                    </Text>
                  </Pressable>
                );
              }}
            />
          </View>

          <Pressable onPress={loadCounties} style={styles.ghostBtn}>
            <Text style={styles.ghostBtnText}>Refresh</Text>
          </Pressable>
        </View>
      )}

      {/* Status tabs */}
      <View style={styles.row}>
        {STATUS_TABS.map((s) => (
          <Pressable key={s} onPress={() => setStatus(s)} style={[styles.pill, status === s && styles.pillActive]}>
            <Text style={[styles.pillText, status === s && styles.pillTextActive]}>{s.toUpperCase()}</Text>
          </Pressable>
        ))}
      </View>

      {/* Search + add */}
      <View style={styles.row}>
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search cities…"
          style={[styles.input, { flex: 1 }]}
        />
        <Pressable onPress={openAdd} style={styles.primaryBtn}>
          <Text style={styles.primaryBtnText}>+ Add</Text>
        </Pressable>
      </View>

      {/* toggles */}
      <View style={styles.row}>
        <Pressable onPress={() => setIncludeDeleted((v) => !v)} style={[styles.toggle, includeDeleted && styles.toggleOn]}>
          <Text style={[styles.toggleText, includeDeleted && styles.toggleTextOn]}>
            Include Deleted: {includeDeleted ? "ON" : "OFF"}
          </Text>
        </Pressable>

        <Pressable onPress={loadCities} style={styles.ghostBtn}>
          <Text style={styles.ghostBtnText}>Refresh</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading…</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
          renderItem={({ item }) => (
            <View style={[styles.card, item.isDeleted && { opacity: 0.6 }]}>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>
                  {item.name} {item.isDeleted ? <Text style={styles.badge}>DELETED</Text> : null}
                </Text>
                <Text style={styles.meta}>status: {item.status} · id: {item.id}</Text>
              </View>

              <View style={styles.actions}>
                <Pressable onPress={() => openEdit(item)} style={styles.btn}>
                  <Text style={styles.btnText}>Edit</Text>
                </Pressable>

                {item.status === "pending" ? (
                  <>
                    <Pressable onPress={() => approve(item)} style={[styles.btn, styles.btnGood]}>
                      <Text style={[styles.btnText, styles.btnTextGood]}>Approve</Text>
                    </Pressable>
                    <Pressable onPress={() => reject(item)} style={[styles.btn, styles.btnBad]}>
                      <Text style={[styles.btnText, styles.btnTextBad]}>Reject</Text>
                    </Pressable>
                  </>
                ) : null}

                <Pressable onPress={() => toggleDelete(item)} style={[styles.btn, styles.btnBad]}>
                  <Text style={[styles.btnText, styles.btnTextBad]}>{item.isDeleted ? "Restore" : "Delete"}</Text>
                </Pressable>
              </View>
            </View>
          )}
          ListEmptyComponent={
            <View style={{ padding: 16 }}>
              <Text style={{ opacity: 0.7 }}>
                {selectedCounty ? `No cities for "${selectedCounty.name}".` : "Pick a county."}
              </Text>
            </View>
          }
        />
      )}

      <NamePromptModal
        visible={modalOpen}
        title={modalMode === "add" ? "Add City (Approved)" : "Edit City"}
        description={
          modalMode === "add"
            ? `County: ${selectedCounty?.name ?? "-"} (created as APPROVED)`
            : "Rename the city (keeps same ID)."
        }
        initialValue={modalMode === "edit" ? editing?.name : ""}
        confirmText={modalMode === "add" ? "Create" : "Save"}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleSaveName}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  selectorRow: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 10 },
  selectorLabel: { fontWeight: "900" },
  selectorWrap: { flex: 1 },

  catPill: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#fafafa",
  },
  catPillActive: { backgroundColor: "#111", borderColor: "#111" },
  catPillText: { fontWeight: "900", fontSize: 12 },
  catPillTextActive: { color: "white" },

  row: { flexDirection: "row", gap: 10, alignItems: "center", marginBottom: 10 },
  pill: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#fafafa",
  },
  pillActive: { backgroundColor: "#111", borderColor: "#111" },
  pillText: { fontWeight: "900", fontSize: 12 },
  pillTextActive: { color: "white" },

  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },

  primaryBtn: {
    backgroundColor: "#111",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  primaryBtnText: { color: "white", fontWeight: "900" },

  ghostBtn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  ghostBtnText: { fontWeight: "900" },

  toggle: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: "#fafafa",
  },
  toggleOn: { backgroundColor: "#111", borderColor: "#111" },
  toggleText: { fontWeight: "900" },
  toggleTextOn: { color: "white" },

  loadingRow: { flexDirection: "row", gap: 8, alignItems: "center", padding: 12 },

  card: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 14,
    padding: 12,
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
    backgroundColor: "white",
  },
  cardTitle: { fontSize: 15, fontWeight: "900" },
  badge: { fontSize: 12, fontWeight: "900", opacity: 0.6 },
  meta: { marginTop: 4, opacity: 0.7, fontSize: 12 },

  actions: { gap: 8, alignItems: "flex-end" },
  btn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  btnText: { fontWeight: "900" },
  btnGood: { borderColor: "#2e7d32" },
  btnTextGood: { color: "#2e7d32" },
  btnBad: { borderColor: "#c62828" },
  btnTextBad: { color: "#c62828" },
});